import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  clampCopilotPanelWidth,
  readCopilotPanelWidth,
  writeCopilotPanelWidth,
} from "@/shared/components/layout/copilot-width";

interface CopilotLayoutValue {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  hasOpened: boolean;
  /** Full-page mode: the conversation covers the app instead of docking beside it. */
  isExpanded: boolean;
  toggleExpanded: () => void;
  width: number;
  setWidth: (width: number) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

/** Read by `analytics-consent-banner.tsx` (mounted outside the router) so it stays clear of the dock. */
const COPILOT_DOCK_INSET_VAR = "--copilot-dock-inset";

const CopilotLayoutContext = createContext<CopilotLayoutValue | null>(null);

export function CopilotLayoutProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [width, setPanelWidth] = useState(readCopilotPanelWidth);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const open = useCallback(() => {
    setHasOpened(true);
    setIsOpen(true);
  }, []);
  // Reopening lands in the dock, the lighter mode, whatever the last session ended in.
  const close = useCallback(() => {
    setIsOpen(false);
    setIsExpanded(false);
  }, []);
  const toggleExpanded = useCallback(() => setIsExpanded((expanded) => !expanded), []);
  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [close, isOpen, open]);
  const setWidth = useCallback((nextWidth: number) => {
    const clamped = clampCopilotPanelWidth(nextWidth);
    setPanelWidth(clamped);
    writeCopilotPanelWidth(clamped);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(COPILOT_DOCK_INSET_VAR, isOpen ? `${width}px` : "0px");
    return () => {
      root.style.removeProperty(COPILOT_DOCK_INSET_VAR);
    };
  }, [isOpen, width]);

  return (
    <CopilotLayoutContext.Provider
      value={{
        isOpen,
        toggle,
        open,
        close,
        hasOpened,
        isExpanded,
        toggleExpanded,
        width,
        setWidth,
        triggerRef,
      }}
    >
      {children}
    </CopilotLayoutContext.Provider>
  );
}

export function useCopilotLayout(): CopilotLayoutValue {
  const context = useContext(CopilotLayoutContext);
  if (!context) throw new Error("useCopilotLayout must be used within CopilotLayoutProvider.");
  return context;
}
